import { useMemo } from "react";
import dayjs from "dayjs";

const messages = {
  // --- FRESH SESSION STATES (first open of the day) ---
  // These appear regardless of time of day
  fresh_empty: [
    "Good to see you. What's on your radar today?",
    "New day. What's worth your focus?",
    "What are you working on today?",
    "Start by picking one thing that matters.",
    "What does a good day look like from here?",
  ],

  fresh_with_carryover: [
    "You've got some things carrying over. Pick up where you left off, or start fresh.",
    "A few tasks from before are still open. Worth a look.",
    "Some unfinished business waiting. Let's get your bearings.",
    "Carried-over tasks are here when you're ready.",
    "A couple of things from before are still open. Start there or add something new.",
  ],

  // --- ONGOING SESSION STATES ---
  empty: {
    dawn: [
      "Early start. What's worth doing today?",
      "Quiet morning. Good time to set your targets.",
      "What does today need from you?",
      "Nothing targeted yet. What's the priority today?",
      "Morning is open. What are you working on?",
    ],
    morning: [
      "No targets yet. What's the focus today?",
      "What would make today feel useful?",
      "Add a target and give the day some direction.",
      "What needs doing today?",
      "Start with one thing. What is it?",
    ],
    afternoon: [
      "It's afternoon and targets are clear. What are we focusing on today?",
      "What's worth doing before the day closes out?",
      "Let's give the afternoon a target.",
      "What's one thing worth finishing today?",
      "Still time to make the day count.",
    ],
    evening: [
      "Anything to wrap up or plan for tomorrow?",
      "Day's winding down. Any targets to set for tomorrow?",
      "Anything to note before tomorrow?",
      "Set tomorrow's targets now while it's fresh.",
      "What do you want to do tomorrow?",
    ],
    dusk: [
      "Almost done for the day. Set tomorrow's targets before you close out.",
      "What's tomorrow's first move?",
      "Plant something for tomorrow before the day ends.",
      "Anything worth capturing before tomorrow?",
      "Set one target for tomorrow and you're done.",
    ],
    night: [
      "It's late. If you've got something on your mind, write it down.",
      "Anything to capture before sleep?",
      "Note it down now, deal with it tomorrow.",
      "One thing for tomorrow, then rest.",
      "If it's in your head, let's create a task for it.",
    ],
  },

  unstarted: {
    dawn: [
      "Targets set. First one's always the hardest.",
      "Tasks are waiting. Pick the easiest one and let's get started.",
      "Ready when you are.",
      "Everything's lined up. What's first?",
      "Set up and ready. Let's get started.",
    ],
    morning: [
      "Tasks are set. Time to pick one and start.",
      "What's the first task? Let's do that.",
      "It's a new day. Let's get started.",
      "What are we working on first?",
      "Tasks are targeted. Which one are we starting with?",
    ],
    afternoon: [
      "Which task are we starting with this afternoon?",
      "Now is as good a time as any to start. Let's pick a task.",
      "One simple task is all it takes to get going.",
      "Let's start with whatever feels most approachable.",
      "Afternoon's here. Which task are we tackling first?",
    ],
    evening: [
      "If you want to start, there's still time to complete a task.",
      "One task before the day closes. That's all we need.",
      "Evening's here. The day isn't over yet.",
      "Last call for today. What can we tick off the list?",
      "One small thing before the day ends.",
    ],
    dusk: [
      "The day is closing out, but is there something still worth doing?",
      "If there's one you can knock out quickly, now's the time.",
      "One task before you close out.",
      "Last chance for today.",
      "Quick one before you're done?",
    ],
    night: [
      "Late to start, but what's on your mind?",
      "If sleep isn't coming, what can we get done before you rest?",
      "Here if you need it. No pressure.",
      "Late but still open.",
      "Up late? One small task might help.",
    ],
  },

  started: {
    dawn: [
      "Moving early. We love to see it.",
      "Already made a dent. Keep going.",
      "Progress at this hour. Nothing is stopping you.",
      "Early momentum is real momentum.",
      "First task done. What's next?",
    ],
    morning: [
      "We are moving. Let's keep going.",
      "Good morning progress. What's next?",
      "You're moving. Don't stop.",
      "One in. What's after that?",
      "Targets are getting hit. Keep the pace.",
    ],
    afternoon: [
      "Making progress. Keep pushing.",
      "Afternoon and you're moving. Good work.",
      "Some done, more to go. Stay with it.",
      "Partway through. How does it feel?",
      "Momentum's there. Let's keep it up.",
    ],
    evening: [
      "You've made a start today. Keep it going if you can.",
      "Some progress is real progress.",
      "Made a dent. That matters.",
      "Done some good work. What else?",
      "Still moving in the evening. That's a good sign.",
    ],
    dusk: [
      "Good effort today. A little more if you have it.",
      "Some targets hit today. Wrap up or push on.",
      "You moved things forward. That's what counts.",
      "Decent day. Finish if you want, rest if you need.",
      "Progress made. Call it or keep going.",
    ],
    night: [
      "Made progress today. Time to rest?",
      "Good effort. Don't forget to rest too.",
      "You ticked something off. That's what we want to see.",
      "Solid work today. Time to recharge.",
      "Progress is progress. Rest well.",
    ],
  },

  progressing: {
    dawn: [
      "More than halfway before the day's properly started.",
      "Strong early work. Keep it going.",
      "Past the halfway mark already.",
      "Momentum is real, you're rolling now",
      "Over halfway through your targets. Finish line's in sight.",
    ],
    morning: [
      "Looking good this morning. Keep it up.",
      "Past halfway before lunch. Looking good.",
      "You're on a roll today.",
      "Good pace this morning.",
      "Almost there. Keep going.",
    ],
    afternoon: [
      "Ticking things off this afternoon. Keep it up.",
      "You're making good progress this afternoon.",
      "Strong afternoon. What's next?",
      "Toppling targets this afternoon. Keep the momentum.",
      "Past halfway. Don't let up.",
    ],
    evening: [
      "Most of your targets are done. Strong day.",
      "Nearly there. Can we finish it off?",
      "Good evening progress. Almost clear.",
      "Close to clearing the list.",
      "Evening push is paying off. Keep it up.",
    ],
    dusk: [
      "Almost done for the day.",
      "Nearly cleared. Worth finishing.",
      "So close. Last push?",
      "Almost there. Let's finish it off.",
      "Good work today. Let's wrap it up.",
    ],
    night: [
      "Strong day. Rest well.",
      "Nearly cleared. Take the win.",
      "Most of it done. That's a good day.",
      "Solid work today. Sleep.",
      "Good day's work. Rest.",
    ],
  },

  cleared: {
    dawn: [
      "Clear before sunrise. Day's yours.",
      "Everything done already. Go again or enjoy the space.",
      "Clean list at dawn. That's rare.",
      "Done. What now?",
      "Full clear. The day is open.",
    ],
    morning: [
      "Everything done before lunch. Nice.",
      "Clean list. What's next?",
      "All cleared. That's a good morning.",
      "Done and dusted. What's next?",
      "List cleared. Enjoy the day.",
    ],
    afternoon: [
      "All done. Afternoon is yours.",
      "Clean list mid-afternoon. Anything else worth doing?",
      "Everything cleared. Good work.",
      "Targets finished. Let's take a moment.",
      "Targets cleared. Well done.",
    ],
    evening: [
      "Clean list at the end of day. How does that feel?",
      "Everything done. Enjoy the evening.",
      "All cleared. Rest easy.",
      "Targets cleared. What's next or is it time to rest?",
      "Done. Enjoy the evening.",
    ],
    dusk: [
      "Targets cleared. Day well spent.",
      "All done. Time to wind down",
      "Ticked everything off. You earned the rest.",
      "Done and done.",
      "Everything finished. Nice.",
    ],
    night: [
      "All done. Sleep well.",
      "Clean list. Rest easy.",
      "Nothing left. Good night.",
      "Full clear. You earned the rest.",
      "Everything done. That's a good feeling.",
    ],
  },
};

/**
 * Derives context variables from tasks to determine first-open state
 */
export function deriveContextVariables(tasks = [], isFirstOpenToday = false) {
  const todayStart = dayjs().startOf("day");

  // Filter targeted tasks by when they were targeted
  const tasksSetToday = tasks.filter(
    (t) =>
      t.targeted &&
      t.targetedAt &&
      !dayjs(t.targetedAt).isBefore(todayStart) &&
      !t.completed,
  ).length;

  // Tasks targeted before today, or targeted tasks without a timestamp (legacy data)
  const tasksSetPreviously = tasks.filter(
    (t) =>
      t.targeted &&
      !t.completed &&
      (!t.targetedAt || dayjs(t.targetedAt).isBefore(todayStart)),
  ).length;

  // Count completed tasks today
  const completedToday = tasks.filter(
    (t) =>
      t.completed &&
      (typeof t.completed === "number"
        ? !dayjs(t.completed).isBefore(todayStart)
        : false),
  ).length;

  return {
    isFirstOpenToday,
    tasksSetToday,
    tasksSetPreviously,
    completedToday,
  };
}

/**
 * Determines the task state based on context variables
 */
function getTaskState({
  isFirstOpenToday,
  tasksSetToday,
  tasksSetPreviously,
  completedToday,
}) {
  // totalToday includes completed tasks since they are removed from the active
  // task store and the completed-tasks API doesn't return the 'targeted' field.
  const totalToday = tasksSetToday + tasksSetPreviously + completedToday;

  // Fresh session states
  if (isFirstOpenToday && totalToday === 0) return "fresh_empty";
  if (isFirstOpenToday && tasksSetPreviously > 0 && completedToday === 0) {
    return "fresh_with_carryover";
  }

  // Ongoing session states
  if (totalToday === 0) return "empty";
  if (completedToday === 0) return "unstarted";
  if (completedToday >= totalToday) return "cleared";
  if (completedToday / totalToday >= 0.5) return "progressing";
  return "started";
}

/**
 * Selects a message based on task state and time of day
 */
function getDayMessage(timeOfDay, taskState) {
  // Fresh states are time-agnostic
  if (["fresh_empty", "fresh_with_carryover"].includes(taskState)) {
    const pool = messages[taskState];
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // Ongoing states are time-aware
  const pool = messages[taskState]?.[timeOfDay] || messages.empty.morning;
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function useDayMessages(timeOfDay, contextVariables) {
  return useMemo(() => {
    const taskState = getTaskState(contextVariables);
    return getDayMessage(timeOfDay, taskState);
  }, [timeOfDay, contextVariables]);
}
